import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseMetaColumn, BaseMetaTable, FilterItem, FilterItemOperator } from "namirasoft-core";
import { ChatRow, RunRow, RunStatus } from "namirasoft-expert";
import { MoneyFormatter, NSButton, NSButtonRed, NSCopy, NSLoading } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { Route, Routes, useParams } from "react-router-dom";
import ChatAgent from "../components/ChatAgent";
import ChatSidebar from "../components/ChatSidebar";
import ChatTest from "../components/ChatTest";
import { ExpertLayout } from "../ExpertLayout";
import { ExpertRouterProps } from "../ExpertRouterProps";
import { ExpertRoutes } from "../ExpertRoutes";
import Styles from "./ChatPage.module.css";

interface ChatPageInnerProps extends NSARouterMakerResult, ExpertRouterProps
{
    agent_id: string;
    chat_id: string;
}

interface ChatPageInnerState
{
    chat: ChatRow | null;
    runs: RunRow[] | null;
    chatLoading: boolean;
}

export class ChatPageInner extends Component<ChatPageInnerProps, ChatPageInnerState>
{
    private ChatSidebar_Ref = createRef<ChatSidebar>();
    static sortRuns(runs: RunRow[]): RunRow[]
    {
        return [...runs].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
    }
    constructor(props: ChatPageInnerProps)
    {
        super(props);
        this.state = {
            chat: null,
            runs: null,
            chatLoading: false,
        };
        this.handleRunChanged = this.handleRunChanged.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
    }

    private handleRunChanged(run: RunRow)
    {
        const current = this.state.runs;
        if (!current)
            return;
        const exists = current.some(r => r.id === run.id);
        const next = exists
            ? current.map(r => r.id === run.id ? run : r)
            : [...current, run];
        this.setState({ runs: ChatPageInner.sortRuns(next) });
    }

    private getInProgressRun(): RunRow | undefined
    {
        return this.state.runs?.find(r =>
            r.status === RunStatus.Pending ||
            r.status === RunStatus.Running ||
            r.status === RunStatus.Waiting
        );
    }

    private async handleCancel(onFinished: () => void)
    {
        try
        {
            const run = this.getInProgressRun();
            if (!run)
                return;
            await this.props.servers.expert.run.Cancel(run.id);
        }
        catch
        {
            // The server already reported the error through the notifier.
        }
        finally
        {
            onFinished();
        }
    }

    override componentDidMount(): void
    {
        this.loadChat();
        this.loadRuns();
    }

    override componentDidUpdate(prevProps: ChatPageInnerProps): void
    {
        if (prevProps.chat_id !== this.props.chat_id)
        {
            if (this.props.chat_id)
            {
                this.loadChat();
                this.loadRuns();
            }
            else
                this.setState({ chat: null, runs: [], chatLoading: false });
        }
    }

    private async loadChat()
    {
        if (this.props.chat_id)
        {
            try
            {
                let table = new BaseMetaTable(null, "chat", "Chat");
                let column = new BaseMetaColumn(table, "id", "ID", "string", true);
                let filters = [
                    new FilterItem(table, column, false, FilterItemOperator.all.equals, this.props.chat_id),
                ];
                let response = await this.props.servers.expert.chat.List(filters, 1, 1, [], null);
                this.setState({ chat: response.rows.length > 0 ? response.rows[0] : null, chatLoading: false });
            }
            catch
            {
                this.setState({ chat: null, chatLoading: false });
            }
        }
        else
            this.setState({ chat: null, chatLoading: false });
    }

    private async loadRuns()
    {
        if (this.props.chat_id)
        {
            this.setState({ runs: null });
            try
            {
                let table = new BaseMetaTable(null, "run", "Run");
                let column = new BaseMetaColumn(table, "chat_id", "Chat ID", "string", true);
                let filters = [
                    new FilterItem(table, column, false, FilterItemOperator.all.equals, this.props.chat_id),
                ];
                let response = await this.props.servers.expert.run.List(filters, 1, 100, [], null);
                this.setState({ runs: ChatPageInner.sortRuns(response.rows) });
            }
            catch
            {
                this.setState({ runs: [] });
            }
        }
        else
            this.setState({ runs: [] });
    }

    override render()
    {
        const childRuns = this.props.chat_id ? (this.state.runs ?? []) : [];
        const childChat = this.props.chat_id ? this.state.chat : null;
        return (
            <ExpertLayout {...this.props}>
                <section className={Styles.ns_chat_page}>
                    <NSButton
                        onClick={{
                            showLoading: false,
                            action: () => this.ChatSidebar_Ref.current?.setOpen(true),
                        }}
                        classList={[Styles.ns_chat_btn]}
                        icon={{ src: "https://static.namirasoft.com/image/concept/menu/burger-blue.svg", alt: "Open Menu" }}
                    />

                    <ChatSidebar
                        {...this.props}
                        ref={this.ChatSidebar_Ref}
                        agent_id={this.props.agent_id}
                        servers={this.props.servers}
                        onItemClick={(path) => this.props.url.redirect(path, {})}
                    />
                    <div className={Styles.ns_chat_content}>

                        {
                            childChat &&
                            <div className={Styles.ns_chat_header}>
                                <h2>{childChat.name}</h2>
                                <span>{childChat.type}</span>
                            </div>
                        }


                        {
                            childChat &&
                            <div className={Styles.ns_chat_footer}>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Chat ID:</span>
                                    <span><NSCopy>{childChat.id}</NSCopy></span>
                                </div>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Created:</span>
                                    <span>{new Date(childChat.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
                                </div>

                                <span className={Styles.ns_chat_spacer} />

                                <div className={`${Styles.ns_chat_stat} ${Styles.ns_chat_stat_bold}`}>
                                    <span className={Styles.ns_chat_stat_label}>Total Tokens:</span>
                                    <span className={Styles.ns_chat_stat_value}>{childChat.total_token.toLocaleString()}</span>
                                </div>
                                <span className={Styles.ns_chat_separator}>=</span>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Input:</span>
                                    <span className={Styles.ns_chat_stat_value}>{childChat.total_token_input.toLocaleString()}</span>
                                </div>
                                <span className={Styles.ns_chat_separator}>+</span>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Output:</span>
                                    <span className={Styles.ns_chat_stat_value}>{childChat.total_token_output.toLocaleString()}</span>
                                </div>

                                <div className={`${Styles.ns_chat_stat} ${Styles.ns_chat_stat_bold}`}>
                                    <span className={Styles.ns_chat_stat_label}>Total Cost:</span>
                                    <span className={Styles.ns_chat_stat_value}>{new MoneyFormatter().format(childChat.total_cost)}</span>
                                </div>
                                <span className={Styles.ns_chat_separator}>=</span>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Input:</span>
                                    <span className={Styles.ns_chat_stat_value}>{new MoneyFormatter().format(childChat.total_cost_input)}</span>
                                </div>
                                <span className={Styles.ns_chat_separator}>+</span>
                                <div className={Styles.ns_chat_stat}>
                                    <span className={Styles.ns_chat_stat_label}>Output:</span>
                                    <span className={Styles.ns_chat_stat_value}>{new MoneyFormatter().format(childChat.total_cost_output)}</span>
                                </div>
                            </div>
                        }

                        {
                            (!this.props.chat_id || this.state.runs != null) ?
                                <Routes>
                                    <Route path="" element={
                                        <ChatAgent
                                            key={this.props.chat_id || "new"}
                                            {...this.props}
                                            runs={childRuns}
                                            onRunChanged={this.handleRunChanged}
                                            onChatChanged={chat => this.setState({ chat })}
                                        />
                                    }
                                    />
                                    <Route
                                        path={ExpertRoutes.Chat_Relative.Agent()}
                                        element={
                                            <ChatAgent
                                                key={this.props.chat_id || "new"}
                                                {...this.props}
                                                runs={childRuns}
                                                onRunChanged={this.handleRunChanged}
                                                onChatChanged={chat => this.setState({ chat })}
                                            />
                                        }
                                    />
                                    <Route path={ExpertRoutes.Chat_Relative.Test()} element={
                                        <ChatTest
                                            key={this.props.chat_id || "new"}
                                            {...this.props}
                                            runs={childRuns}
                                            onRunChanged={this.handleRunChanged}
                                            onChatChanged={chat => this.setState({ chat })}
                                        />
                                    }
                                    />
                                </Routes>
                                :
                                <NSLoading />
                        }

                        {
                            this.getInProgressRun() &&
                            <div className={Styles.ns_chat_cancel}>
                                <NSButtonRed
                                    title="Cancel"
                                    onClick={{
                                        showLoading: true,
                                        action: (onFinished) => this.handleCancel(onFinished)
                                    }}
                                />
                            </div>
                        }
                    </div>
                </section>
            </ExpertLayout >
        );
    }
}

interface ChatPageProps extends NSARouterMakerResult, ExpertRouterProps { }

function ChatPage(props: ChatPageProps)
{
    let params = useParams();
    let agent_id = params["agent_id"] ?? "";
    let chat_id = params["chat_id"] ?? "";
    if (chat_id === "new")
        chat_id = "";
    return <ChatPageInner {...props} agent_id={agent_id} chat_id={chat_id} />;
}

export default ChatPage;